import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { detectUserCurrency, formatCurrency, getAvailableCurrencies } from '../utils/geolocation';
import { convertCurrency, getSuggestedAmounts } from '../utils/currencyConverter';

const CurrencyContext = createContext();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [isDetecting, setIsDetecting] = useState(true);
  const [detectionResult, setDetectionResult] = useState(null);
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [isReady, setIsReady] = useState(false);

  // Initialize currency detection and available currencies
  useEffect(() => {
    const initializeCurrency = async () => {
      try {
        setIsDetecting(true);
        
        // Load available currencies
        const currencies = getAvailableCurrencies();
        setAvailableCurrencies(currencies);

        // Auto-detect user's currency
        const result = await detectUserCurrency();
        setDetectionResult(result);
        setSelectedCurrency(result.currency);
        
        console.log('Currency auto-detected:', result);
      } catch (error) {
        console.error('Currency detection failed:', error);
        // Fallback to USD
        setDetectionResult({
          currency: 'USD',
          country: 'United States',
          method: 'fallback'
        });
        setSelectedCurrency('USD');
      } finally {
        setIsDetecting(false);
        setIsReady(true);
      }
    };

    initializeCurrency();
  }, []);

  // Convert amount from USD to current currency
  const convertFromUSD = useCallback(async (usdAmount) => {
    if (!usdAmount || usdAmount <= 0) return 0;
    if (selectedCurrency === 'USD') return usdAmount;
    
    try {
      return await convertCurrency(usdAmount, 'USD', selectedCurrency);
    } catch (error) {
      console.error('Currency conversion failed:', error);
      return usdAmount;
    }
  }, [selectedCurrency]);

  // Convert amount from current currency to USD
  const convertToUSD = useCallback(async (amount) => {
    if (!amount || amount <= 0) return 0;
    if (selectedCurrency === 'USD') return amount;
    
    try {
      return await convertCurrency(amount, selectedCurrency, 'USD');
    } catch (error) {
      console.error('Currency conversion failed:', error);
      return amount;
    }
  }, [selectedCurrency]);

  // Format amount with current currency
  const formatAmount = useCallback((amount, precision = 2) => {
    return formatCurrency(amount, selectedCurrency);
  }, [selectedCurrency]);

  // Get suggested amounts in current currency
  const getSuggestedAmountsInCurrency = useCallback(async (baseAmounts = [10, 25, 50, 100, 250, 500]) => {
    try {
      return await getSuggestedAmounts(selectedCurrency, baseAmounts);
    } catch (error) {
      console.error('Failed to get suggested amounts:', error);
      return baseAmounts.map(amount => ({
        original: amount,
        converted: amount,
        currency: selectedCurrency
      }));
    }
  }, [selectedCurrency]);

  // Change currency and convert existing amounts
  const changeCurrency = useCallback(async (newCurrency) => {
    if (newCurrency === selectedCurrency) return;
    
    setSelectedCurrency(newCurrency);
    
    // Update detection result
    if (detectionResult) {
      setDetectionResult({
        ...detectionResult,
        currency: newCurrency,
        method: 'manual'
      });
    }
  }, [selectedCurrency, detectionResult]);

  // Get currency info
  const getCurrencyInfo = useCallback(() => {
    const currency = availableCurrencies.find(c => c.code === selectedCurrency);
    return currency || { code: 'USD', symbol: '$', name: 'US Dollar', country: 'United States' };
  }, [selectedCurrency, availableCurrencies]);

  // Get conversion rate for display
  const getConversionRate = useCallback(async () => {
    if (selectedCurrency === 'USD') return 1.0;
    try {
      const rate = await convertCurrency(1, 'USD', selectedCurrency);
      return rate;
    } catch (error) {
      return 1.0;
    }
  }, [selectedCurrency]);

  const value = {
    // State
    selectedCurrency,
    isDetecting,
    detectionResult,
    availableCurrencies,
    isReady,
    
    // Actions
    changeCurrency,
    convertFromUSD,
    convertToUSD,
    formatAmount,
    getSuggestedAmountsInCurrency,
    getCurrencyInfo,
    getConversionRate,
    
    // Utilities
    formatCurrency: formatAmount,
    currencySymbol: getCurrencyInfo().symbol,
    currencyCode: selectedCurrency
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
